import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/Button';
import { InviteShell } from '../auth/AcceptInviteLayout';
import { SignupHeader } from './SignupLayout';
import { SignOutButton } from './SignupSignOutButton';
import { useEnterApp } from './useEnterApp';
import { usePaymentActivation } from './usePaymentActivation';

/** Router state this page reads to explain why it is waiting. Set by `/signup/payment`. */
export interface SignupCompleteState {
  paymentPending?: boolean;
}

const PAYMENT_PENDING_MESSAGE =
  "Your payment is still processing — we'll activate your account as soon as it clears.";

/**
 * Stripe Checkout's success URL (`/signup/complete?session_id=...`), and where `/signup/payment`
 * sends a coach whose earlier payment is still processing.
 *
 * The session id is not read: the server already knows which organization this admin owns, and the
 * webhook, not this page, is what unlocks it. The page only waits for that to happen, then sends the
 * coach into the app. Because anyone signed in can open this URL, nothing here claims a payment was
 * received; it only reports what the status endpoint says.
 */
export const SignupCompletePage = () => {
  const location = useLocation();
  const paymentPending = (location.state as SignupCompleteState | null)?.paymentPending === true;
  const { state, retry } = usePaymentActivation();
  const { enterApp, entering, error } = useEnterApp();
  const enteredRef = useRef(false);

  useEffect(() => {
    if (state === 'active' && !enteredRef.current) {
      enteredRef.current = true;
      void enterApp();
    }
  }, [state, enterApp]);

  if (state === 'signInRequired') {
    return <SessionExpired />;
  }

  if (state === 'refused') {
    return (
      <InviteShell>
        <SignupHeader title="We could not check your account" />
        <div
          className="accept-invite-message accept-invite-message--error"
          role="alert"
          data-testid="signup-activation-refused"
        >
          <p>
            We couldn&apos;t check this account&apos;s signup. If you signed up with a different
            account, sign out and sign in with that one.
          </p>
        </div>
        <div className="accept-invite-actions">
          <SignOutButton />
        </div>
      </InviteShell>
    );
  }

  if (state === 'timedOut') {
    return (
      <InviteShell>
        <SignupHeader title="Your account isn't active yet" />
        <div
          className="accept-invite-message accept-invite-message--warning"
          data-testid="signup-activation-timeout"
        >
          {paymentPending ? (
            <p>{PAYMENT_PENDING_MESSAGE}</p>
          ) : (
            <p>
              We haven&apos;t confirmed a payment for your account yet. If you just paid, it can take a
              minute to go through.
            </p>
          )}
          <p>Check again shortly.</p>
        </div>
        <div className="accept-invite-actions">
          <Button type="button" fullWidth onClick={retry} data-testid="signup-activation-retry">
            Check again
          </Button>
          <Link to="/signup/payment" className="accept-invite-link" data-testid="signup-return-to-payment">
            Return to payment
          </Link>
        </div>
      </InviteShell>
    );
  }

  if (state === 'active') {
    return (
      <InviteShell>
        <SignupHeader title="Your account is active" />
        {error && (
          <div className="accept-invite-message accept-invite-message--error" role="alert">
            <p>{error}</p>
          </div>
        )}
        <div className="accept-invite-actions">
          <Button
            type="button"
            fullWidth
            loading={entering}
            onClick={() => void enterApp()}
            data-testid="signup-enter-app"
          >
            Go to RosterPilot
          </Button>
        </div>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <SignupHeader title="Finishing your signup" focusOnMount={false} />
      <div className="accept-invite-loading" role="status" data-testid="signup-activation-polling">
        <p>
          {paymentPending
            ? PAYMENT_PENDING_MESSAGE
            : 'Confirming your payment. This usually takes a few seconds.'}
        </p>
      </div>
    </InviteShell>
  );
};

/**
 * The status check answered 401: the session is gone, so polling can never succeed. Sign-in comes
 * back here (the return path), where the wait resumes with a fresh token.
 */
const SessionExpired = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleSignIn = async () => {
    setBusy(true);
    try {
      // Unconditionally, as SignInInsteadButton does: AuthContext's user lags behind Firebase (it
      // waits on a profile request), so "no user" there does not mean no session. Signing out with
      // none is harmless.
      await logout();
    } catch {
      // /login is still reachable; a lingering session is routed on from there.
    } finally {
      setBusy(false);
      navigate('/login', { replace: true, state: { from: '/signup/complete' } });
    }
  };

  return (
    <InviteShell>
      <SignupHeader title="Sign in to continue" />
      <div
        className="accept-invite-message accept-invite-message--warning"
        role="alert"
        data-testid="signup-activation-signed-out"
      >
        <p>Your session has expired. Sign in again to check on your account.</p>
      </div>
      <div className="accept-invite-actions">
        <Button
          type="button"
          fullWidth
          loading={busy}
          onClick={() => void handleSignIn()}
          data-testid="signup-activation-sign-in"
        >
          Sign in
        </Button>
      </div>
    </InviteShell>
  );
};
